"use client";

import { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import Kicker from "@/src/components/common/broadsheet/kicker";
import {
  FONT_DISPLAY,
  FONT_UI,
} from "@/src/components/common/broadsheet/tokens";

const WORDS_PER_MINUTE = 200;

const countWords = (html: string) => {
  const text = html
    .replace(/<[^>]*>/g, " ")
    .replace(/&[a-z#0-9]+;/gi, " ")
    .trim();
  return text ? text.split(/\s+/).length : 0;
};

const TimeProgress = ({ contentText }: { contentText: string }) => {
  const [progress, setProgress] = useState(0);

  const totalMinutes = useMemo(() => {
    const words = countWords(contentText || "");
    return words ? Math.max(1, Math.round(words / WORDS_PER_MINUTE)) : 0;
  }, [contentText]);

  useEffect(() => {
    let frame = 0;

    const measure = () => {
      frame = 0;
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      const next = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, Math.round(next))));
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  if (!totalMinutes) return null;

  const remaining = Math.max(0, Math.ceil(totalMinutes * (1 - progress / 100)));

  return (
    <Box sx={{ display: { xs: "none", lg: "block" }, mb: 1.25 }}>
      <Kicker sx={{ mb: 0.25 }}>Reading</Kicker>
      <Box
        sx={{
          fontFamily: FONT_UI,
          fontSize: "0.75rem",
          color: "custom.inkMuted",
          mb: 0.3125,
        }}
      >
        <Box
          component="span"
          sx={{
            fontFamily: FONT_DISPLAY,
            fontSize: "1.125rem",
            fontWeight: 700,
            color: "custom.link",
            mr: 0.25,
          }}
        >
          {progress}%
        </Box>
        read · {remaining} min left
      </Box>
      <Box
        role="progressbar"
        aria-label="Reading progress"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
        sx={{
          height: "3px",
          backgroundColor: "custom.ruleHairline",
        }}
      >
        <Box
          sx={{
            width: `${progress}%`,
            height: "100%",
            backgroundColor: "primary.main",
            transition: "width .1s linear",
          }}
        />
      </Box>
    </Box>
  );
};

export default TimeProgress;
